/**
 * The main modal view. Only one of me should exist in any interface. Otherwise, you might
 * be doing modals wrong ( stacked ).
 * @class
 */
import Base from '@netx/core/lib/netx-multiView';
import {appRouter} from '@netx/core-next/instance/router';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';
import Template from '../templates/appModalView.html';
import AppModalCrumbsView from './appModalCrumbsView';
import ConfirmView from './confirmView';

const logger = getPrefixedLogger('views/appFullScreenView');

class AppModalView extends Base {
	/** @override */
	constructor(opts = {}) {
		opts.attributes = _.extend(
			{
				tabindex: '-1',
				role: 'dialog',
				'aria-hidden': 'true',
				'aria-labelledby': 'app-modal',
			},
			opts.attributes,
		);
		opts.childView = _.extend(
			{
				viewClass(model) {
					return model.get('viewClass');
				},
				target: '[data-nx-control="modal-content"]',
			},
			opts.childView,
		);
		opts.className = opts.className || 'modal fade';
		opts.emptyOnHide = opts.emptyOnHide !== false;
		opts.events = _.extend(
			{
				'click [data-nx-control="close"]:first': 'close',
			},
			opts.events,
		);
		opts.hideOnEmpty = opts.hideOnEmpty !== false;
		opts.id = opts.id || 'app-modal';
		opts.removeOnHide = false;
		opts.modalOptions = _.extend(
			{
				size: 'md',
			},
			opts.modalOptions,
		);
		opts.template = opts.template || Template;
		super(opts);

		// Hey, listen!
		this.listenTo(dispatcher, 'routed', this.hideIfWeShould);
		// A new child view was added - check if it was caused by a route
		this.listenTo(this, 'addedNewChildView', (view) => {
			if (view.viewSpec.isMyOriginalCollection(this.viewCollection)) {
				// Check if the view was a result of routing
				if (view.isRouted !== false && !(view instanceof ConfirmView)) {
					// view.isRouted = true;
				}
			}
		});

		// Crumbs
		this.modalCrumbsView = this.addSubView(
			new AppModalCrumbsView({
				collection: this.viewCollection,
			}),
		);

		this.activeViewClassNames = '';
	}

	/**
	 * @override
	 * @param {event} e Keyup event
	 */
	escape(e) {
		!this.blocking && this.hide();
	}

	/**
	 * This is where the any animation should happen
	 * When the callback is executed the app considers this view as good as hidden
	 * Overriding for bootstrap modal behavior
	 * @override
	 * @param {function} cb Callback
	 */
	beforeHidden(cb) {
		// We do not call super
		if (this.emptyOnHide) {
			let view;
			this.viewCollection.each((viewSpec) => {
				view = viewSpec.get('view');
				view && _.isFunction(view.cancel) && view.cancel();
			});
			// Always use set to empty
			this.viewCollection.set([]);
		}

		this.$backdrop && this.$backdrop.removeClass('in');

		_.delay(() => {
			this.$backdrop && this.$backdrop.remove();
			this.$backdrop = null;
			// Trigger animation
			this.$el.removeClass('in').attr('aria-hidden', true);
			// Give css animations time to finish
			_.delay(() => {
				this.$body && this.$body.removeClass('modal-open');
				// Reset scrollbar
				this.$body.css('padding-right', '');
				this.$el.hide();
				cb.call(self);
			}, 300);
		}, 150);
	}

	/** @override */
	postRender() {
		super.postRender();

		// Cache
		this.$crumbs = this.$('[data-nx-control="crumbs"]');
		this.$title = this.$('[data-nx-control="modal-title"]');
		this.$dialog = this.$('.modal-dialog');
		this.$close = this.$('[data-nx-control="close"]');
		this.$content = this.$('[data-nx-control="modal-content"]');
		this.$body = $('body');

		// Set default size
		this.$dialog
			.removeClass('modal-md modal-sm modal-lg')
			.addClass(`modal-${this.modalOptions.size}`);

		// Crumbs
		this.modalCrumbsView.setElement(this.$crumbs).render();
	}

	/**
	 * This is where the any animation should happen
	 * When the callback is executed the app considers this view as good as shown
	 * Overriding for bootstrap modal behavior
	 * @override
	 * @param {function} cb Callback
	 */
	beforeShown(cb) {
		if (this.$backdrop || !this.viewCollection.length) return;

		// Create and append backdrop
		this.$backdrop = $('<div>', {
			class: 'modal-backdrop fade',
		});
		this.$backdrop.appendTo(this.$body);
		this.$body.addClass('modal-open');
		// Set scrollbar
		const bodyPad = parseInt(this.$body.css('padding-right') || 0, 10);
		if (this.scrollbarWidth) {
			this.$body.css('padding-right', bodyPad + this.scrollbarWidth);
		}
		this.$backdrop.appendTo(this.$body);
		// Force reflow
		this.$backdrop[0].offsetWidth;
		this.$backdrop.addClass('in');

		_.delay(() => {
			// Modal
			this.$el.show().scrollTop(0);
			// Force reflow
			this.$el[0].offsetWidth;
			this.$el.addClass('in').attr('aria-hidden', false);
			this.$el.trigger('focus');
			_.delay(_.bind(cb, self), 300);
		}, 150);
	}

	/**
	 * Show is about to start
	 * At this point no animation or actual showing [should] have taken place
	 * @override
	 * @listens Netx.View#showing
	 */
	onShowing() {
		if (this.viewCollection.length === 0) {
			this.hide();
			return;
		}

		super.onShowing();
		this.hiddenByRoute = false;
	}

	/**
	 * @method
	 * @param {string} newRoute New route
	 * @param {string} oldRoute Old route
	 */
	hideIfWeShould(newRoute, oldRoute) {
		if (!this.hiddenByRoute && !this.hiding && !this.showing) {
			// We will need to modify this as time goes on and we understand the complexity of
			// the workflow for wizards and such
			const activeView = this.getActiveView();
			if (this.viewCollection.length > 1) {
				if (newRoute !== oldRoute) {
					this.viewCollection.each((viewSpec) => {
						const view = viewSpec.get('view');
						if (activeView && view.cid === activeView.cid) return;
						if (
							view &&
							view.isRouted &&
							!_.isUndefined(view.routeMatch) &&
							!_.isNull(oldRoute.match(view.routeMatch))
						) {
							if (
								view.hasFunctionality('form') &&
								viewSpec.isMyOriginalCollection(this.viewCollection)
							) {
								view.cancel();
							} else {
								view.hide();
							}
						}
					});
				}
			}
			if (
				this.isShown &&
				!this.hiding &&
				!_.isUndefined(activeView) &&
				activeView.isRouted
			) {
				// if we should not be showing on this route
				if (!activeView.isMyRoute(window.location.hash.slice(1))) {
					this.hiddenByRoute = true;
					// To save people from themselves we always have a slight hide delay
					// Meaning the view must live for at least 500ms
					$.when(activeView.hideDelayed ? activeView.hideDelayed : true).then(
						() => {
							_.delay(() => {
								// If we are the only view - just hide the modal and not go through
								// the hiding of the view followed by the modal
								if (!this.hiding && this.viewCollection.length === 1) {
									activeView.$el.fadeOut('fast');
									this.hide();
									return;
								} else {
									if (
										activeView.viewSpec.isMyOriginalCollection(
											this.viewCollection,
										) &&
										activeView.hasFunctionality('form')
									) {
										activeView.cancel();
									} else {
										activeView.hide();
									}
								}
							}, 20);
						},
					);
				}
			}
		}
		// Wait to be shown and then recheck
		else if (this.showing) {
			this.whenShown.then(() => {
				return this.hideIfWeShould(newRoute, oldRoute);
			});
		}
	}

	/**
	 * Show will be starting soon
	 * At this point not even `view.show` has been executed
	 * This allows the view to do anything special it needs to do in anticipation of calling `view.show`
	 * @method
	 * @param {Netx.View} view Instance of Netx.View
	 * @listens Netx.View.MultiView#view:showing
	 */
	beforeShowView(view) {
		////////////////////////////////
		// UPDATE FROM MODAL OPTIONS
		////////////////////////////////
		const modalOpts = view.modalOptions || {};

		// Update size
		this.$dialog &&
			this.$dialog
				.removeClass('modal-md modal-sm modal-lg')
				.addClass('modal-' + (modalOpts.size || this.modalOptions.size));

		// Prevent the content area from jumping
		const currentHeight = this.$childTargetEl.height();
		if (currentHeight) {
			this.$childTargetEl.height(currentHeight);
		}

		// Update blocking
		this.blocking = modalOpts.blocking === true;
		this.$el.toggleClass('blocking', this.blocking);
		// Update title - will look to the view's modal options first and then fallback to the view's title
		this.$title && this.$title.html(modalOpts.headerTitle || view.headerTitle);

		// Clean up and apply any new classNames
		this.$el.removeClass(this.activeViewClassNames);
		this.activeViewClassNames = modalOpts.classNames || '';
		this.$el.addClass(this.activeViewClassNames);
		////////////////////////////////
		// !UPDATE FROM MODAL OPTIONS
		////////////////////////////////

		return super.beforeShowView(view);
	}

	/**
	 * @method
	 * @param {event} e Click event
	 */
	close(e) {
		!this.blocking && this.hide();
	}

	/**
	 * @override
	 * @param {Netx.View} view Instance of `Netx.View`
	 * @listens {@link Netx.Views.MultiView#view:shown}
	 */
	viewAfterShown(view) {
		super.viewAfterShown(view);

		// Reset height
		view.rendered.then(() => {
			this.$childTargetEl.height('');
		});
	}

	/**
	 * Provided with the view we are changing to and the knowledge of the current view
	 * you can block the change by returning `false`
	 * @override
	 * @param {Netx.View} view Instance of Netx.View we are changing to
	 * @returns {boolean} Ok to change/show view
	 */
	viewChanging(view) {
		const activeView = this.getActiveView();
		const modalOpts = view.modalOptions || {};
		// What blocks a change:
		// We must have a currently active view - we must have more than 1 view and the current view mustnot be blocking
		// unless the view trying to show is blocking - since it is new it takes precedent
		return (
			modalOpts.blocking ||
			!activeView ||
			this.viewCollection.length === 1 ||
			activeView.stickyModal !== true
		);
	}

	/**
	 * A view was instantiated (a brand new childView was born)
	 * @override
	 * @param {Netx.View} view Instance of Netx.View
	 */
	viewInstantiated(view) {
		super.viewInstantiated(view);

		// All child views will be fully removed on hide
		if (view.viewSpec.isMyOriginalCollection(this.viewCollection)) {
			view.removeOnHide = true;
			view.removeOnSimpleHide = false;
		}
		// Sticky? - TODO: Revisit the difference between sticky and blocking - I got confused I think - I am
		const modalOpts = view.modalOptions || {};
		view.stickyModal = modalOpts.blocking === true;
	}

	/**
	 * @override
	 * @param {Netx.View} view Instance of Netx.View
	 */
	viewRemoved(view) {
		if (
			!this.hiddenByRoute &&
			view.viewSpec.isMyOriginalCollection(this.viewCollection)
		) {
			// If a new view came in in the mean time and it is routed - do nothing - we are at the route we should be
			const activeView = this.getActiveView();
			if (activeView && activeView.cid !== view.cid && activeView.isRouted) {
				return;
			}
			// HACK: preserve this value across callbacks:
			// (isRouting() reports false if a callback is fired via setTimeout(), such as in certain Bootstrap hiding animations.)
			const isRouting = Backbone.history.isRouting();
			// If we opened this modal view due to a route...
			if (view.isRouted) {
				logger.silly('removing routed view >', view.viewSpec.id);

				if (view.viewSpec.isMyOriginalCollection(this.viewCollection)) {
					delete view.isRouted;
				}
				// Unless we're currently routing, route back to where we were before we opened the modal.
				if (!isRouting) {
					if (!_.isUndefined(view.routeMatch)) {
						appRouter.exitRoute(view.routeMatch);
					} else {
						appRouter.back();
					}
				}
			}
		}
		delete this.hiddenByRoute;

		super.viewRemoved(view);
	}

	/**
	 * @static
	 * @type {string}
	 */
	static get __name__() {
		return 'AppModalView';
	}
}

export default AppModalView;
